import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { formatApiErrorDetail } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import TranscriberTaskCard from '../components/TranscriberTaskCard';
import { toast } from 'sonner';
import { BarChart3, Zap, Target, DollarSign, CheckCircle, Briefcase, Loader2 } from 'lucide-react';

export default function TranscriberDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const { data: d } = await api.get('/transcriber/dashboard');
      setData(d);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handleAccept = async (doc) => {
    setClaiming(doc.id);
    try {
      await api.post(`/documents/${doc.id}/claim`);
      toast.success('Task claimed! Opening workspace...');
      navigate(`/workspace/${doc.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setClaiming(null);
    }
  };

  const handleOpen = (doc) => {
    if (doc.status === 'assigned' || doc.status === 'typing') {
      navigate(`/workspace/${doc.id}`);
    } else {
      navigate(`/document/${doc.id}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 size={32} className="animate-spin text-[var(--brand-primary)]" />
      </div>
    );
  }

  const stats = data?.stats || {};

  return (
    <div className="pb-nav" data-testid="transcriber-dashboard">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[var(--brand-text)] mb-6">
          Transcriber Dashboard
        </h1>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <StatBox icon={Target} label="Score" value={`${((stats.score || 0) * 100).toFixed(0)}%`} />
          <StatBox icon={Zap} label="Speed" value={`${stats.wpm || 0} WPM`} />
          <StatBox icon={CheckCircle} label="Done" value={stats.tasks_completed || 0} />
        </div>

        <div className="flex justify-between items-center bg-white border border-[var(--brand-border)] p-4 mb-6">
          <div className="flex items-center gap-2">
            <DollarSign size={18} className="text-[var(--brand-accent)]" />
            <span className="text-sm font-semibold text-[var(--brand-text)]">Balance</span>
          </div>
          <span className="text-2xl font-black font-mono text-[var(--brand-primary)]" data-testid="transcriber-balance">
            ${(stats.balance || 0).toFixed(2)}
          </span>
        </div>

        <Tabs defaultValue="available">
          <TabsList className="w-full border-b border-[var(--brand-border)] bg-transparent p-0 rounded-none h-auto mb-6">
            <TabsTrigger
              value="available"
              data-testid="tab-available"
              className="px-4 py-3 text-sm font-medium text-[var(--brand-text-sec)] data-[state=active]:text-[var(--brand-primary)] data-[state=active]:border-b-2 data-[state=active]:border-[var(--brand-primary)] rounded-none data-[state=active]:shadow-none data-[state=active]:bg-transparent"
            >
              Available
              {(data?.available?.length || 0) > 0 && (
                <Badge className="ml-2 bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] border-none rounded-sm text-[10px]">
                  {data.available.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="active"
              data-testid="tab-active"
              className="px-4 py-3 text-sm font-medium text-[var(--brand-text-sec)] data-[state=active]:text-[var(--brand-primary)] data-[state=active]:border-b-2 data-[state=active]:border-[var(--brand-primary)] rounded-none data-[state=active]:shadow-none data-[state=active]:bg-transparent"
            >
              Active
              {(data?.active?.length || 0) > 0 && (
                <Badge className="ml-2 bg-[var(--brand-warning)]/10 text-[var(--brand-warning)] border-none rounded-sm text-[10px]">
                  {data.active.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="completed"
              data-testid="tab-completed"
              className="px-4 py-3 text-sm font-medium text-[var(--brand-text-sec)] data-[state=active]:text-[var(--brand-primary)] data-[state=active]:border-b-2 data-[state=active]:border-[var(--brand-primary)] rounded-none data-[state=active]:shadow-none data-[state=active]:bg-transparent"
            >
              Completed
            </TabsTrigger>
          </TabsList>

          <TabsContent value="available">
            {(data?.available || []).length === 0 ? (
              <EmptyState message="No available tasks right now" />
            ) : (
              <div className="space-y-3">
                {data.available.map(doc => (
                  <TranscriberTaskCard
                    key={doc.id}
                    doc={doc}
                    onAccept={handleAccept}
                    onOpen={handleOpen}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="active">
            {(data?.active || []).length === 0 ? (
              <EmptyState message="No active tasks" />
            ) : (
              <div className="space-y-3">
                {data.active.map(doc => (
                  <TranscriberTaskCard key={doc.id} doc={doc} onOpen={handleOpen} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="completed">
            {(data?.completed || []).length === 0 ? (
              <EmptyState message="No completed tasks yet" />
            ) : (
              <div className="space-y-3">
                {data.completed.map(doc => (
                  <TranscriberTaskCard key={doc.id} doc={doc} onOpen={handleOpen} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatBox({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-[var(--brand-border)] p-4 text-center">
      <Icon size={18} className="text-[var(--brand-text-sec)] mx-auto mb-1" />
      <p className="text-[10px] text-[var(--brand-text-sec)] uppercase tracking-wider">{label}</p>
      <p className="text-lg font-black font-mono text-[var(--brand-text)]">{value}</p>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="text-center py-12">
      <Briefcase size={40} className="mx-auto text-[var(--brand-border)] mb-3" />
      <p className="text-[var(--brand-text-sec)]">{message}</p>
    </div>
  );
}
